import useSWR, { type SWRConfiguration, type SWRResponse } from "swr";
import { api } from "./api";

/**
 * SWR wrapper.
 *
 * Key convention: a plain path string, or `[path, ...scope]` when the response
 * depends on something outside the URL. The extra members take part in cache
 * identity only - the fetcher reads the path and ignores the rest.
 *
 * **The active site needs no special handling here.** A site-scoped request
 * names its site in the path (`/sites/12/users`), so switching sites changes the
 * key by changing the URL, and the cache separates the two lists without being
 * told to.
 *
 * Pass `null` to skip conditionally:
 *   useApi<SiteList>(canReadSites ? "/sites" : null)
 */
export type ApiKey = string | readonly [string, ...unknown[]];

const pathOf = (key: ApiKey): string => (Array.isArray(key) ? (key[0] as string) : (key as string));

export const fetcher = <T>(key: ApiKey): Promise<T> => api<T>(pathOf(key));

export const swrDefaults: SWRConfiguration = {
  fetcher,
  dedupingInterval: 5_000,
  // The console is a monitoring surface people leave open on a second screen;
  // refetching every time it regains focus is noise, not freshness.
  revalidateOnFocus: false,
  shouldRetryOnError: true,
  errorRetryCount: 2,
  errorRetryInterval: 4_000,
};

export function useApi<T = unknown>(
  key: ApiKey | null,
  config?: SWRConfiguration<T>
): SWRResponse<T, Error> {
  return useSWR<T, Error>(key, config);
}
